import {useSearchParams} from 'react-router-dom';
import {Info} from 'lucide-react';
import {Alert, AlertDescription, AlertTitle} from '@kroll-resolver-crisp/ui-components-react';
import {
  SUBMISSIONS_DASHBOARD_DETAIL_BLOCKS,
  SUBMISSIONS_DASHBOARD_COLLAPSIBLE_BLOCKS
} from './submissionsDashboardLayout';
import {useSubmissionsDashboard} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {type ReportQuery} from '@/api/reporting';
import {LoadingState, ReportControls} from '@/components/reporting/ReportControls';
import {selectedValues} from '@/components/reporting/ReportParameters';
import {ReportRenderer} from '@/components/reporting/ReportRenderer';

const GENERATED_AT = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short'
});

// The filters live in the URL, so a view can be pasted to a colleague and reloaded. Empty values are
// dropped rather than sent: the API then applies its own default.
function queryFrom(searchParams: URLSearchParams): ReportQuery {
  return Object.fromEntries([...searchParams.entries()].filter(([, value]) => value !== ''));
}

/**
 The Submissions dashboard: what has been sent to the client and what the platform did about it.
 Across every vertical only the global blocks are shown (volumes, outcomes, quality, delivery); the
 classification, language and type breakdowns say little at that level, so they appear once a vertical
 is chosen.
 */
export function SubmissionsDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = queryFrom(searchParams);
  const dashboard = useSubmissionsDashboard(query);
  const {data} = dashboard;

  if (dashboard.isLoading) {
    return <LoadingState title="Loading the dashboard…" />;
  }

  // What is on screen, not what is being typed: the breakdowns follow the filters last run.
  const verticalChosen = selectedValues(data?.parameter_values.vertical).length > 0;
  const blocks =
    data?.blocks.filter(
      (block) => verticalChosen || !SUBMISSIONS_DASHBOARD_DETAIL_BLOCKS.has(block.id)
    ) ?? [];

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="mx-auto max-w-screen-2xl space-y-6 px-4 py-6">
        <ReportControls
          key={JSON.stringify(query)}
          autoRun
          summary={{
            id: data?.id ?? 'submissions_dashboard',
            title: data?.title ?? 'Submissions dashboard',
            ...(data?.description ? {description: data.description} : {})
          }}
          parameters={data?.parameters ?? []}
          applied={query}
          running={dashboard.isFetching}
          onRun={(next) => {
            const nonEmpty = Object.entries(next).filter(([, value]) => value !== '');
            // Re-running unchanged filters is a deliberate refresh, which a URL no-op would swallow.
            if (JSON.stringify(nonEmpty) === JSON.stringify(Object.entries(query))) {
              void dashboard.refetch();
            } else {
              setSearchParams(nonEmpty, {replace: true});
            }
          }}
        />

        {dashboard.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Dashboard failed</AlertTitle>
            <AlertDescription>{getApiErrorMessage(dashboard.error)}</AlertDescription>
          </Alert>
        ) : null}

        {data ? (
          <>
            {verticalChosen ? null : (
              <Alert>
                <Info />
                <AlertTitle>Showing every vertical</AlertTitle>
                <AlertDescription>
                  Choose a vertical to see its classifications, languages and types.
                </AlertDescription>
              </Alert>
            )}
            <div
              aria-busy={dashboard.isFetching}
              className={dashboard.isFetching ? 'opacity-60 transition-opacity' : undefined}>
              <ReportRenderer
                splitKpis
                report={{...data, blocks}}
                collapsibleBlocks={SUBMISSIONS_DASHBOARD_COLLAPSIBLE_BLOCKS}
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Generated {GENERATED_AT.format(new Date(data.generated_at))} for{' '}
              {Object.entries(data.parameter_values)
                .map(([name, value]) => `${name} ${value}`)
                .join(', ')}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
